import React from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

interface VisitorInfoFormProps {
  documentType: "dni" | "passport" | "other";
  documentNumber: string;
  firstName: string;
  lastName: string;
  loadingNameData: boolean;
  formError: string | null;
  onDocumentTypeChange: (type: "dni" | "passport" | "other") => void;
  onDocumentNumberChange: (value: string) => void;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  onValidate: () => void;
  isSubmitting: boolean;
  documentNumberLength: number;
  validationError: string | null;
  hasValidated: boolean;
  isNameEditable: boolean;
}

export const VisitorInfoForm: React.FC<VisitorInfoFormProps> = ({
  documentType,
  documentNumber,
  firstName,
  lastName,
  loadingNameData,
  formError,
  onDocumentTypeChange,
  onDocumentNumberChange,
  onFirstNameChange,
  onLastNameChange,
  onBack,
  onSubmit,
  onValidate,
  isSubmitting,
  documentNumberLength,
  validationError,
  hasValidated,
  isNameEditable,
}) => {
  return (
    <Box>
      <Box
        component="img"
        src="/images/DomusDesktop.png"
        alt="Domus logo"
        sx={{
          width: { xs: 200, sm: 250 },
          height: "auto",
          marginTop: 5,
          marginBottom: 7,
          display: "block",
          mx: "auto",
        }}
      />
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Button
          onClick={onBack}
          sx={{
            minWidth: "auto",
            p: 1,
            color: "#000000",
            "&:hover": {
              bgcolor: "#f5f5f5",
            },
          }}
        >
          <ArrowBack />
        </Button>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "#000000",
            flex: 1,
          }}
        >
          Información del visitante
        </Typography>
      </Stack>

      {(formError || validationError) && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "6px" }}>
          {formError || validationError}
        </Alert>
      )}

      <Stack spacing={2.5}>
        <FormControl fullWidth>
          <InputLabel id="document-type-label">Tipo de documento</InputLabel>
          <Select
            labelId="document-type-label"
            value={documentType}
            label="Tipo de documento"
            onChange={(e) =>
              onDocumentTypeChange(e.target.value as "dni" | "passport" | "other")
            }
            sx={{
              borderRadius: "6px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
            }}
          >
            <MenuItem value="dni">DNI</MenuItem>
            <MenuItem value="passport">Pasaporte</MenuItem>
            <MenuItem value="other">Otro</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Número de documento"
          value={documentNumber}
          onChange={(e) => onDocumentNumberChange(e.target.value)}
          placeholder="Ingrese su número de documento"
          helperText="Ingresa tu documento para continuar con el registro"
          disabled={hasValidated}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#e0e0e0",
              },
            },
            "& .MuiFormHelperText-root": {
              color: "#666666",
            },
          }}
        />

        {hasValidated && (
          <>
            <TextField
              fullWidth
              label="Nombre"
              value={firstName}
              onChange={(e) => isNameEditable && onFirstNameChange(e.target.value)}
              disabled={!isNameEditable}
              placeholder={firstName ? undefined : "Ingrese su nombre"}
              helperText={
                firstName
                  ? "Nombre censurado por seguridad"
                  : "Ingrese su nombre si no se autocompletó"
              }
              slotProps={{
                input: {
                  sx: { borderRadius: "6px" },
                  endAdornment: loadingNameData ? <CircularProgress size={20} /> : null,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                  },
                },
                "& .MuiFormHelperText-root": {
                  color: "#999999",
                },
              }}
            />

            <TextField
              fullWidth
              label="Apellidos"
              value={lastName}
              onChange={(e) => isNameEditable && onLastNameChange(e.target.value)}
              disabled={!isNameEditable}
              placeholder={lastName ? undefined : "Ingrese sus apellidos"}
              helperText={
                lastName
                  ? "Apellido censurado por seguridad"
                  : "Ingrese sus apellidos si no se autocompletó"
              }
              slotProps={{
                input: {
                  sx: { borderRadius: "6px" },
                  endAdornment: loadingNameData ? <CircularProgress size={20} /> : null,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                  },
                },
                "& .MuiFormHelperText-root": {
                  color: "#999999",
                },
              }}
            />
          </>
        )}

        {!hasValidated ? (
          <Button
            onClick={onValidate}
            variant="contained"
            fullWidth
            disabled={
              isSubmitting ||
              !documentNumber ||
              documentNumberLength < 5 ||
              loadingNameData
            }
            sx={{
              bgcolor: "#000000",
              color: "#ffffff",
              borderRadius: "6px",
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#333333",
                boxShadow: "none",
              },
              "&:disabled": {
                bgcolor: "#e0e0e0",
                color: "#999999",
              },
            }}
          >
            {loadingNameData ? "Validando..." : "Validar DNI"}
          </Button>
        ) : (
          <Button
            onClick={onSubmit}
            variant="contained"
            fullWidth
            disabled={isSubmitting || !firstName || !lastName}
            sx={{
              bgcolor: "#000000",
              color: "#ffffff",
              borderRadius: "6px",
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#333333",
                boxShadow: "none",
              },
              "&:disabled": {
                bgcolor: "#e0e0e0",
                color: "#999999",
              },
            }}
          >
            {isSubmitting ? "Procesando..." : "Aceptar invitación"}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
